import ApexCharts, { ApexOptions } from "apexcharts";
import React, { useEffect, useRef } from "react";
import { getCSS } from "../../../assets/ts/_utils";
import { useThemeMode } from "../../layout/theme-mode/ThemeModeProvider";

type Props = {
  className: string;
  labels: string[];
  dataList: number[];
  title: string;
  eyebrow?: string;
  description?: string;
};

const UsersPerMonthChart: React.FC<Props> = ({
  className,
  labels,
  dataList,
  title,
  eyebrow,
  description,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const { mode } = useThemeMode();
  const hasData = dataList.some((value) => value > 0);

  useEffect(() => {
    if (!chartRef.current || !labels.length || !dataList.length || !hasData) {
      return;
    }

    const height = parseInt(getCSS(chartRef.current, "height"));
    const chart = new ApexCharts(
      chartRef.current,
      getBarChartOptions(height, labels, dataList),
    );
    chart.render();

    return () => {
      chart.destroy();
    };
  }, [chartRef, mode, labels, dataList, hasData]);

  return (
    <div className={`card dashboard-chart-card ${className}`}>
      <div className="card-header border-0 pb-0">
        <div className="dashboard-chart-card__heading">
          {eyebrow ? (
            <span className="dashboard-chart-card__eyebrow">{eyebrow}</span>
          ) : null}
          <h3 className="dashboard-chart-card__title">{title}</h3>
          <p className="dashboard-chart-card__description">
            {description ?? "Monthly user registration trends"}
          </p>
        </div>
      </div>
      <div className="card-body dashboard-chart-card__body">
        {!hasData && (
          <div className="dashboard-chart-empty">
            <span className="dashboard-chart-empty__icon">UP</span>
            <span className="dashboard-chart-empty__copy">
              <span className="dashboard-chart-empty__title">
                No user registrations yet
              </span>
              <span className="dashboard-chart-empty__subtitle">
                New registrations will start filling this monthly trend view.
              </span>
            </span>
          </div>
        )}
        <div
          ref={chartRef}
          className="dashboard-chart-canvas"
          style={{ height: "350px", opacity: hasData ? 1 : 0.22 }}
        ></div>
      </div>
    </div>
  );
};

export { UsersPerMonthChart };

function getBarChartOptions(
  height: number,
  labels: string[],
  dataList: number[],
): ApexOptions {
  const maxVal = Math.max(...dataList, 1);
  return {
    series: [{ name: "Users", data: dataList }],
    chart: {
      fontFamily: "inherit",
      type: "bar",
      height,
      toolbar: { show: false },
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 800,
        animateGradually: { enabled: true, delay: 80 },
      },
    },
    colors: ["#0F766E"],
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "vertical",
        shadeIntensity: 0.15,
        gradientToColors: ["#38BDF8"],
        inverseColors: false,
        opacityFrom: 0.96,
        opacityTo: 0.82,
        stops: [0, 100],
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "44%",
        borderRadius: 12,
        dataLabels: {
          position: "top",
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val: number) {
        return val > 0 ? val.toString() : "";
      },
      offsetY: -14,
      style: { fontSize: "12px", fontWeight: 800, colors: ["#0F172A"] },
      background: {
        enabled: false,
        dropShadow: {
          enabled: false,
        },
      },
      dropShadow: {
        enabled: false,
      },
    },
    legend: { show: false },
    stroke: { show: true, width: 0, colors: ["transparent"] },
    xaxis: {
      categories: labels,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        rotate: 0,
        style: { colors: "#64748B", fontSize: "12px", fontWeight: 600 },
      },
    },
    yaxis: {
      min: 0,
      max: maxVal <= 5 ? 5 : undefined,
      forceNiceScale: true,
      labels: {
        style: { colors: "#94A3B8", fontSize: "12px", fontWeight: 500 },
        formatter: function (val: number) {
          return Math.round(val).toString();
        },
      },
    },
    grid: {
      borderColor: "#E2E8F0",
      strokeDashArray: 5,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: 18, right: 8, bottom: 0, left: 8 },
    },
    tooltip: {
      enabled: true,
      theme: "light",
      style: { fontSize: "13px" },
      y: {
        formatter: function (val: number) {
          return val + " user(s)";
        },
      },
      marker: {
        show: false,
      },
    },
    states: {
      hover: {
        filter: { type: "lighten", value: 0.04 },
      },
    },
  };
}
